<template>
  <div class="column content-box">
    <div class="flex-container flex-dir-column medium-flex-dir-row">
      <div class="small-12 medium-3 pull-right">
        <multiselect
          v-model="selectedTimeSlot"
          :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :options="timeSlots"
          :searchable="false"
          :allow-empty="false"
          @select="filterDataByTimeSlot"
        />
      </div>
      <div class="small-12 medium-3 pull-right margin-left-small">
        <multiselect
          v-model="selectedDynamicOption"
          :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
          label="name"
          track-by="id"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :options="groupedDynamicOptions"
          :searchable="true"
          :allow-empty="false"
          @select="filterDataByDynamicOption"
        />
      </div>
      <div class="small-12 medium-12 pull-right margin-left-small">
        <report-date-range-selector @date-range-change="onDateRangeChange" />
      </div>
    </div>
    
    <div class="row">
      <woot-report-stats-card
        v-for="(metric, index) in metrics"
        :key="metric.NAME"
        :desc="metric.DESC"
        :heading="metric.NAME"
        :index="index"
        :on-click="changeSelection"
        :point="getTotalPoints(index)"
        :selected="index === currentSelection"
      />
    </div>
    
    <div class="row" v-if="uniqueAdmins.length">
      <div class="admin-names">
        <h3>Admin Names:</h3>
        <p>{{ uniqueAdmins.join(', ') }}</p>
      </div>
    </div>

    <div class="report-bar">
      <woot-loading-state v-if="isFetching" :message="$t('REPORT.LOADING_CHART')" />
      <div v-else class="chart-container">
        <woot-bar v-if="chartData.length" :collection="collection" />
        <span v-else class="empty-state">
          {{ $t('REPORT.NO_ENOUGH_DATA') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ReportDateRangeSelector from './components/DateRangeSelector';
import { addDays, fromUnixTime, format } from 'date-fns';
import axios from 'axios';

export default {
  components: {
    ReportDateRangeSelector,
  },
  data() {
    return {
      from: 0,
      to: 0,
      currentSelection: 0,
      reportData: [],
      isFetching: false,
      chartData: [],
      selectedTimeSlot: 'All',
      timeSlots: ['All', '01.00 - 07.00 น.', '7.00 - 13.00 น.', '13.00 - 19.00 น.', '19.00 - 01.00 น.'],
      selectedDynamicOption: null,
      dynamicOptions: [],
      groupedDynamicOptions: [],
      uniqueAdmins: [],
    };
  },
  computed: {
    ...mapGetters({
      accountSummary: 'getAccountSummary',
    }),
    collection() {
      if (this.isFetching || !this.chartData.length) {
        return {};
      }
      const labels = this.chartData.map(element =>
        format(fromUnixTime(element.timestamp), 'dd/MMM')
      );
      const data = this.chartData.map(element => element.value);
      return {
        labels,
        datasets: [
          {
            label: this.metrics[this.currentSelection].NAME,
            backgroundColor: '#1f93ff',
            data,
          },
        ],
      };
    },
    metrics() {
      return [
        { NAME: '1s - 2 mins', KEY: '1s - 2 mins', DESC: 'Responses' },
        { NAME: '2 mins - 10 mins', KEY: '2 mins - 10 mins', DESC: 'Responses' },
        { NAME: '10 mins - 30 mins', KEY: '10 mins - 30 mins', DESC: 'Responses' },
        { NAME: '> 30 mins', KEY: '> 30 mins', DESC: 'Responses' },
        { NAME: 'No response', KEY: 'No response', DESC: 'No response' },
      ];
    },
    filteredData() {
      return this.reportData.map(day => {
        const filteredTimeSlots = this.selectedTimeSlot === 'All' 
          ? day.time_slots 
          : day.time_slots.filter(slot => slot.time_slot === this.selectedTimeSlot);

        return {
          date: day.date,
          time_slots: filteredTimeSlots,
        };
      }).filter(day => day.time_slots.length > 0);
    }
  },
  methods: {
    async fetchAllData() {
      const { from, to } = this;
      const startDate = format((fromUnixTime(from)), 'yyyy-MM-dd HH:mm:ss');
      const endDate = format(addDays(fromUnixTime(to), 1), 'yyyy-MM-dd HH:mm:ss');
      this.isFetching = true;
      const fetchDataForId = async (startDate, endDate, id) => {
        return axios.get(`https://uat-linebc.pugs.team/api/response?startDate=${startDate}&endDate=${endDate}&inboxId=${id}`);
      };
      try {
        const ids = this.selectedDynamicOption.id.split(',');
        const promises = ids.map(id => fetchDataForId(startDate, endDate, id));
        const responses = await Promise.all(promises);

        this.reportData = this.combineResults(responses.map(response => response.data));

        this.fetchChartData();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.isFetching = false;
      }
    },
    combineResults(dataArrays) {
      const combinedData = [];

      dataArrays.forEach(dataArray => {
        dataArray.forEach(day => {
          let combinedDay = combinedData.find(d => d.date === day.date);
          if (!combinedDay) {
            combinedDay = { date: day.date, time_slots: [] };
            combinedData.push(combinedDay);
          }

          day.time_slots.forEach(slot => {
            let combinedSlot = combinedDay.time_slots.find(s => s.time_slot === slot.time_slot);
            if (!combinedSlot) {
              combinedSlot = { time_slot: slot.time_slot, categories: [] };
              combinedDay.time_slots.push(combinedSlot);
            }

            slot.categories.forEach(category => {
              let combinedCategory = combinedSlot.categories.find(c => c.response_time_category === category.response_time_category && c.admin === category.admin);
              if (!combinedCategory) {
                combinedCategory = { response_time_category: category.response_time_category, count: 0, admin: category.admin };
                combinedSlot.categories.push(combinedCategory);
              }

              combinedCategory.count += parseInt(category.count);
            });
          });
        });
      });

      return combinedData;
    },
    fetchChartData() {
      if (this.filteredData.length) {
        const selectedMetric = this.metrics[this.currentSelection].KEY;
        const newChartData = this.filteredData.map(day => {
          return {
            timestamp: new Date(day.date).getTime() / 1000,
            value: day.time_slots.reduce((sum, slot) => {
              return sum + slot.categories
                .filter(category => category.response_time_category === selectedMetric)
                .reduce((subSum, category) => subSum + parseInt(category.count), 0);
            }, 0)
          };
        });
        this.chartData = newChartData;

        const adminSet = new Set();
        this.filteredData.forEach(day => {
          day.time_slots.forEach(slot => {
            slot.categories.forEach(category => {
              if (category.admin) {
                adminSet.add(category.admin);
              }
            });
          });
        });
        this.uniqueAdmins = Array.from(adminSet);
      } else {
        this.chartData = [];
        this.uniqueAdmins = [];
      }
    },
    changeSelection(index) {
      this.currentSelection = index;
      this.fetchChartData();
    },
    onDateRangeChange({ from, to }) {
      this.from = from;
      this.to = to;
      this.fetchAllData();
    },
    filterDataByTimeSlot() {
      this.fetchChartData();
    },
    filterDataByDynamicOption() {
      this.fetchChartData();
    },
    getTotalPoints(index) {
      const selectedMetric = this.metrics[index].KEY;
      return this.filteredData.reduce((sum, day) => {
        return sum + day.time_slots.reduce((slotSum, slot) => {
          return slotSum + slot.categories
            .filter(category => category.response_time_category === selectedMetric)
            .reduce((categorySum, category) => categorySum + parseInt(category.count), 0);
        }, 0);
      }, 0);
    },
    async fetchDynamicOptions() {
      try {
        const response = await axios.get('https://uat-linebc.pugs.team/api/options');
        this.dynamicOptions = response.data.map(option => ({
          id: option.id,
          name: option.name
        }));
        this.groupSimilarOptions();
      } catch (error) {
        console.error('Error fetching dynamic options:', error);
      }
    },
    groupSimilarOptions() {
      const groupedMap = {};
      const groupedOptions = [];

      this.dynamicOptions.forEach(option => {
        const baseName = option.name.replace(/\d+$/, '').toLowerCase();
        if (!Object.prototype.hasOwnProperty.call(groupedMap, baseName)) {
          groupedMap[baseName] = {
            id: [],
            name: `${baseName.charAt(0).toUpperCase() + baseName.slice(1)}`,
            count: 0,
            originalOptions: []
          };
        }
        groupedMap[baseName].id.push(option.id);
        groupedMap[baseName].count += 1;
        groupedMap[baseName].originalOptions.push(option);
      });

      Object.keys(groupedMap).forEach(baseName => {
        const group = groupedMap[baseName];
        if (group.count >= 3) {
          group.id = group.id.join(',');
          groupedOptions.push(group);
        }
      });

      this.groupedDynamicOptions = groupedOptions;
    }
  },
  watch: {
    selectedTimeSlot() {
      this.fetchChartData();
    },
    selectedDynamicOption() {
      this.fetchAllData();
    },
    currentSelection() {
      this.fetchChartData();
    }
  },
  mounted() {
    this.fetchDynamicOptions();
  }
};
</script>

<style scoped>
.admin-names {
  padding: 10px;
  background-color: white;
  border-radius: 5px;
  width: 100%;
}

.admin-names h3 {
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
}

.admin-names p {
  font-size: 14px;
  color: #666;
}
</style>